import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import AddPaymentModal    from './subscription-payment/AddPaymentModal.js';
import EditPaymentModal   from './subscription-payment/EditPaymentModal.js';
import ViewPaymentModal   from './subscription-payment/ViewPaymentModal.js';
import {
  CreditCard, Pencil, ArrowLeft, Calendar, DollarSign, CheckCircle2,
  XCircle, Building2, FileText, Receipt, Eye, Clock, Plus, Trash2
} from "lucide-react";
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';


const ViewCompanySubscription = () => {
  const { t } = useTranslation();
  const { subscriptionId } = useParams();
  const [subscription, setSubscription]   = useState(null);
  const [payments, setPayments]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const navigate = useNavigate();

  // Modal state
  const [addModal, setAddModal]           = useState(false);
  const [openPaymentActionId, setOpenPaymentActionId] = useState(null);
  const paymentActionRef = useRef(null);
  const [viewPayment, setViewPayment]     = useState(null);
  const [editPayment, setEditPayment]     = useState(null);

  useEffect(() => {
    fetchSubscription();
    fetchPayments();
  }, [subscriptionId]);

  useEffect(() => {
    if (!openPaymentActionId) return undefined;
    const handleClickOutside = (event) => {
      if (paymentActionRef.current && !paymentActionRef.current.contains(event.target)) {
        setOpenPaymentActionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPaymentActionId]);

  const fetchSubscription = async () => {
    try {
      const response = await companySubscriptionService.getById(subscriptionId);
      setSubscription(response.data);
    } catch (err) {
      Alert.error('Failed to load subscription details');
      console.error(err);
      navigate('/company-subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response = await companySubscriptionService.getPaymentsBySubscription(subscriptionId);
      setPayments(response.data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleModalSuccess = () => {
    setAddModal(false);
    setEditPayment(null);
    fetchPayments();
    fetchSubscription(); // refresh balance
  };

  const handleDeletePayment = async (payment) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete payment ${payment.payment_id}? This action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await companySubscriptionService.deletePayment(payment.id);
      Alert.success('Payment deleted successfully!');
      fetchPayments();
      fetchSubscription();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete payment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status, isActive, isExpired) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <CheckCircle2 className="w-4 h-4" /> Active
        </span>
      );
    } else if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <XCircle className="w-4 h-4" /> Expired
        </span>
      );
    } else if (status?.toLowerCase() === 'inactive') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
          <XCircle className="w-4 h-4" /> Inactive
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
          <Clock className="w-4 h-4" /> Upcoming
        </span>
      );
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      pending:   { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      failed:    { color: 'bg-red-100 text-red-700', icon: XCircle },
      refunded:  { color: 'bg-blue-100 text-blue-700', icon: FileText },
      cancelled: { color: 'bg-gray-100 text-gray-700', icon: XCircle },
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase()}
      </span>
    );
  };

  const totalPaid = payments
    .filter(p => p.payment_status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const balance = subscription ? (subscription.subscription_amount || 0) - totalPaid : 0;

  return (
    <DashboardLayout>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading subscription details..." />
        </div>
      ) : !subscription ? null : (
        <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">

                {/* Header Section */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" onClick={() => navigate('/company-subscriptions')} title={t('actions.backToSubscriptions')}>
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                      </Button>
                      <div className="p-2 bg-orange-100 rounded-xl">
                        <CreditCard className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-700">Subscription Details</h2>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">View complete subscription information</p>
                      </div>
                    </div>
                    <Button variant="primary" icon={Pencil} onClick={() => navigate(`/company-subscriptions/edit/${subscriptionId}`)}>
                      Edit Subscription
                    </Button>
                  </div>
                </div>

                {/* Main Content */}
                <div className="p-4 sm:p-6 space-y-6">

                  {/* Subscription Overview Card */}
                  <div className="bg-gradient-to-r from-orange-50 to-white border border-orange-100 rounded-xl p-6">
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                      <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center gap-3 mb-4">
                          <h3 className="text-2xl font-bold text-gray-900">{subscription.subscription_id}</h3>
                          {getStatusBadge(subscription.status, subscription.is_active, subscription.is_expired)}
                          {subscription.is_renewal && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              Renewal
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Building2 className="w-4 h-4" />
                            <span className="font-medium">Company:</span>
                            <span className="text-gray-900 font-semibold">
                              {subscription.company?.company_name || 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">Period:</span>
                            <span className="text-gray-900">
                              {companySubscriptionService.getPeriodDisplayName(
                                subscription.period_type,
                                subscription.custom_years
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-span-2 md:col-span-1 grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Subscription Amount</p>
                          <p className="text-lg font-bold text-orange-600">
                            {formatCurrency(subscription.subscription_amount)}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Total Paid</p>
                          <p className="text-lg font-bold text-green-600">{formatCurrency(totalPaid)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Balance</p>
                          <p className={`text-lg font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(balance)}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Days Remaining</p>
                          <p className="text-lg font-bold text-blue-600">
                            {subscription.is_expired ? '0' : subscription.days_remaining || '0'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Details */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-600" />
                      Subscription Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Subscription ID</p>
                        <p className="text-sm font-semibold text-gray-900">{subscription.subscription_id}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Company ID</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {subscription.company?.company_id || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">From Date</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(subscription.from_date)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">To Date</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(subscription.to_date)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">{t('common.createdBy')}</p>
                        <p className="text-sm font-semibold text-gray-900">{subscription.created_by || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Created At</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(subscription.created_at)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Updated At</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(subscription.updated_at)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Status</p>
                        {getStatusBadge(subscription.status, subscription.is_active, subscription.is_expired)}
                      </div>
                    </div>

                    {subscription.notes && (
                      <div className="mt-4 bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <p className="text-xs text-blue-600 font-medium mb-1">Notes</p>
                        <p className="text-sm text-gray-900">{subscription.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Payment History */}
                  <div>
                    <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-gray-200">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-700 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-orange-600" />
                        Payment History ({payments.length})
                      </h3>
                      <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddModal(true)} disabled={balance <= 0} title={balance <= 0 ? 'Subscription fully paid' : 'Record new payment'}>
                        Add Payment
                      </Button>
                    </div>

                    {paymentsLoading ? (
                      <div className="text-center py-8">
                        <Loader size="small" message="Loading payments..." />
                      </div>
                    ) : payments.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                        <Receipt className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No payments recorded</h3>
                        <p className="mt-1 text-sm text-gray-500">No payment records found for this subscription</p>
                        {balance > 0 && (
                          <Button variant="primary" icon={Plus} onClick={() => setAddModal(true)} className="mt-4 mx-auto">
                            Record First Payment
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {payments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{payment.payment_id}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{formatDate(payment.payment_date)}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {companySubscriptionService.getPaymentMethodDisplayName(payment.payment_method)}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {getPaymentStatusBadge(payment.payment_status)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{payment.transaction_id || '-'}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {payment.has_receipt && payment.receipt_url ? (
                                    <a
                                      href={payment.receipt_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 hover:text-blue-900 transition-colors flex items-center gap-1 text-xs"
                                    >
                                      <FileText className="w-4 h-4" />
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-400">No receipt</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <RowActionsMenu
                                    isOpen={openPaymentActionId === payment.id}
                                    onToggle={() =>
                                      setOpenPaymentActionId(
                                        openPaymentActionId === payment.id ? null : payment.id
                                      )
                                    }
                                    onClose={() => setOpenPaymentActionId(null)}
                                    menuRef={paymentActionRef}
                                    onView={() => setViewPayment(payment)}
                                    onEdit={() => setEditPayment(payment)}
                                    onDelete={() => handleDeletePayment(payment)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="md:hidden space-y-3">
                        {payments.map((payment) => (
                          <div
                            key={payment.id}
                            className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{payment.payment_id}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{formatDate(payment.payment_date)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {getPaymentStatusBadge(payment.payment_status)}
                                <RowActionsMenu
                                  isOpen={openPaymentActionId === payment.id}
                                  onToggle={() =>
                                    setOpenPaymentActionId(
                                      openPaymentActionId === payment.id ? null : payment.id
                                    )
                                  }
                                  onClose={() => setOpenPaymentActionId(null)}
                                  menuRef={paymentActionRef}
                                  onView={() => setViewPayment(payment)}
                                  onEdit={() => setEditPayment(payment)}
                                  onDelete={() => handleDeletePayment(payment)}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-semibold text-gray-900 mt-0.5">{formatCurrency(payment.amount)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Method:</span>
                                <p className="font-medium text-gray-900 mt-0.5">
                                  {companySubscriptionService.getPaymentMethodDisplayName(payment.payment_method)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Transaction:</span>
                                <p className="font-medium text-gray-900 mt-0.5 truncate">{payment.transaction_id || '—'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Receipt:</span>
                                <p className="mt-0.5">
                                  {payment.has_receipt && payment.receipt_url ? (
                                    <a
                                      href={payment.receipt_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-gray-400">None</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      </>
                    )}
                  </div>

                  {/* Bottom Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                    <Button variant="primary" icon={Pencil} onClick={() => navigate(`/company-subscriptions/edit/${subscriptionId}`)}>
                      Edit Subscription
                    </Button>
                    <Button variant="secondary" icon={Building2} onClick={() => navigate(`/companies/view/${subscription.company?.id}`)}>
                      View Company
                    </Button>
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/company-subscriptions')}>
                      Back to List
                    </Button>
                  </div>
                </div>
              </div>
      {/* ── Modals ───────────────────────────────────────────────────── */}

      {addModal && (
        <AddPaymentModal
          subscription={subscription}
          totalPaid={totalPaid}
          onClose={() => setAddModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {viewPayment && (
        <ViewPaymentModal
          payment={viewPayment}
          onClose={() => setViewPayment(null)}
        />
      )}

      {editPayment && (
        <EditPaymentModal
          payment={editPayment}
          subscription={subscription}
          totalPaid={totalPaid}
          onClose={() => setEditPayment(null)}
          onSuccess={handleModalSuccess}
        />
      )}
        </>
      )}
    </DashboardLayout>
  );
};

export default ViewCompanySubscription;